/**
 * LeaveFormModal
 *
 * Congés: request or add a leave, and open one — read-only, with its justificatifs and the
 * validate / reject / delete actions the viewer is entitled to.
 *
 * Laid out in the same FormDialog rows as the arrêt de travail forms (and the permissions
 * page's profile dialog), with the same date field: a date plus a half day, the start in the
 * morning or at noon, the end at noon or in the evening.
 */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Autocomplete, Chip, Stack, TextField, Typography } from "@mui/material";
import CollabTypeChip from "../../components/collabs/CollabTypeChip";
import moment from "moment-timezone";
import Langs, { Localize } from "../../app/lang/langs";
import {
    getLeaves, requestDeleteLeave, requestLeave, submitLeave,
    getLeaveAttachments, uploadLeaveAttachment, deleteLeaveAttachment, clearLeaveAttachments
} from "./leavesSlice";
import NetworkManager from "../../app/managers/networkManager";
import {
    checkPermission, generateUniqueId, getLastUpdatedFromDate, getTagClass, isOwner, isAdmin,
    isCollabPresent, showErrorToast, showSuccessToast
} from "../../app/utils/helpers";
import { getPlanning } from "../planning/planningSlice";
import { getCollabs } from "../../components/collabs/collabsSlice";
import { config } from "../../app/utils/config";
import FormDialog, { FormRow, FormButton, formFieldSx } from "../../components/popups/FormDialog";
import { confirmDialog } from "../../components/popups/ConfirmDialog";
import LeaveDateRangeField, {
    leavePeriodFromTimestamps, leaveTimestampsFromPeriod, periodLabelKey
} from "../../components/inputs/LeaveDateRangeField";
import LeaveTypeSelect from "../../components/inputs/LeaveTypeSelect";
import WorkStoppageAttachments from "../../components/workStoppages/WorkStoppageAttachments";

/** PrimeReact tag severities (getTagClass), as MUI chip colours. */
const STATUS_CHIP_COLORS = { success: "success", warning: "warning", danger: "error", info: "info" };

/** A read-only value in a row. */
function Value({ children, muted }) {
    return (
        <Typography sx={{ fontSize: "0.9rem", color: muted ? "text.secondary" : "text.primary" }}>
            {children}
        </Typography>
    )
}

/** A collab as the picker lists them — the name, never a JSX label (Autocomplete needs text). */
const collabLabel = (collab) => (collab.prenom || collab.nom)
    ? `${collab.prenom || ""} ${collab.nom || ""}`.trim()
    : String(collab.label ?? "")

export function LeaveFormModal(props) {

    const dispatch = useDispatch()

    const userId = useSelector((state) => state.login.userId)
    const userRoleId = useSelector((state) => state.login.userRoleId)
    const userCollabId = useSelector((state) => state.login.userId)
    const teams = useSelector((state) => state.teams.list)
    const currentCollabs = useSelector((state) => state.collabs.currentList)
    const myTeam = useSelector((state) => state.teams.myTeam)
    const sampleLeaves = useSelector((state) => state.sampleLeaves.list)
    const leaveModalType = useSelector((state) => state.leaves.leaveModalType)
    const selectedLeaveFormData = useSelector((state) => state.leaves.selectedLeaveFormData)
    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds)
    const collabs = useSelector((state) => state.collabs.list)
    const attachments = useSelector((state) => state.leaves.attachments)
    const attachmentsLoading = useSelector((state) => state.leaves.attachmentsLoading)
    const attachmentUploading = useSelector((state) => state.leaves.attachmentUploading)
    const isMobile = useSelector((state) => state.settings.isMobile)
    // File chosen while CREATING a leave. It cannot be uploaded yet — the upload endpoint
    // keys on id_planning, which does not exist until the leave is inserted — so it is held
    // here and sent once requestLeave comes back with the new id.
    const [pendingAttachment, setPendingAttachment] = useState(null)

    const [formError, setFormError] = useState(null)
    // A request is in flight: the send button is disabled so it cannot go twice.
    const [saving, setSaving] = useState(false)
    const [selectedCollab, setSelectedCollab] = useState()
    const [formData, setFormData] = useState({})
    const [leavesOptions, setLeavesOptions] = useState([])
    const [timezone, setTimezone] = useState(config.timezone)

    const mounted = useRef(false)

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true
            setNewFormData()
            setSelectedCollab(null)
        }
    })

    useEffect(() => {
        loadLeaves()
    }, [dispatch, collabs])

    // Fetched per opened leave rather than with the leaves listing: the list endpoint is
    // guarded on `leaves` edit for that leave's team, so requesting it for every row would
    // 403 en masse for anyone with view-only rights. Cleared when the leave changes so a
    // previous leave's documents are never on screen while the new fetch is in flight.
    useEffect(() => {
        const idPlanning = selectedLeaveFormData && selectedLeaveFormData.id
        if (leaveModalType === 'UPDATE_LEAVE' && idPlanning && canUseAttachments()) {
            dispatch(getLeaveAttachments(idPlanning))
        } else {
            dispatch(clearLeaveAttachments())
        }
    }, [dispatch, leaveModalType, selectedLeaveFormData, sampleLeaves])

    useEffect(() => {
        if (selectedLeaveFormData && teams) {
            let team = teams.find(t => t.id == selectedLeaveFormData.id_team)
            if (team) {
                switch (team.id_country) {
                    case 3:
                        setTimezone("Indian/Mauritius")
                        break
                    default:
                        setTimezone(config.timezone)
                        break
                }
            }
        }
    }, [selectedLeaveFormData, teams])

    function setNewFormData() {

        let from = moment().tz(timezone).startOf("day")
        let to = moment().tz(timezone).endOf("day")

        let leaveData = {
            id: generateUniqueId(),
            id_team: null,
            id_collab: null,
            id_sample_leave: null,
            type: null,
            from: parseInt(from.format("x")),
            to: parseInt(to.format("x")),
            description: null
        }
        setFormData(leaveData)
    }

    function getCollabsOptions() {
        let uniqueIds = []
        let collabsOptions = currentCollabs.filter((collab) => {
            return isCollabPresent(collab.statut)
                && (!myTeam || checkPermission(config.scopes.leaves, config.permissionTypes.edit, collab.id_team) || collab.id_team != myTeam.id || collab.id == userCollabId)
                && !collab.id_team_delegate
        })
        return collabsOptions.filter(c => {
            if (uniqueIds.includes(c.id)) {
                return false
            }
            uniqueIds.push(c.id)
            return true
        })
    }

    function isManager() {
        let teamId = formData.id_team
        if (selectedLeaveFormData && leaveModalType == 'UPDATE_LEAVE')
            teamId = selectedLeaveFormData.id_team
        return checkPermission(config.scopes.leaves, config.permissionTypes.edit, teamId)
    }

    // The read-only view is opened from two places that carry different row shapes: the
    // leaves table (leavesSlice — `from`/`to` are already display-formatted strings, the
    // timestamps live in `fromDate`/`toDate`) and the planning scheduler (planningSlice —
    // millisecond timestamps in `from`/`to`, with no `fromDate`/`toDate` at all). Read
    // whichever field actually holds the timestamp, and never fall back to parsing the
    // formatted string — that is what rendered "Invalid date".
    function getLeaveTimestamp(leaveData, key) {
        if (!leaveData) return null
        let candidates = [leaveData[key + "Date"], leaveData[key]]
        for (let value of candidates) {
            if (value === null || value === undefined || value === "") continue
            if (typeof value === "number") return value
            if (value instanceof Date) return value.getTime()
            if (typeof value === "string" && /^\d+$/.test(value.trim())) return parseInt(value, 10)
        }
        return null
    }

    function renderLeaveDate(timestamp) {
        if (timestamp === null) return "-"
        return moment(timestamp, "x").tz(timezone).format("DD MMMM YY")
    }

    function hide() {
        setNewFormData()
        setSelectedCollab(null)
        setFormError(null)
        props.onHide()
    }

    function updateLeaveWithStatus(status) {
        dispatch(submitLeave({
            id: selectedLeaveFormData.id,
            status: status
        })).then((response) => {
            if (response['payload']['responseData']) {
                loadPlanning()
            }
            if (response['payload']['responseError']) {
                if (response['payload']['responseError']) {
                    if (response['payload']['responseError']['data'] && response['payload']['responseError']['data']['message']) {
                        showErrorToast(response['payload']['responseError']['data']['message'])
                    } else showErrorToast()
                }
            }
        })
        props.onHide()
    }

    function loadPlanning() {
        if (selectedTeamsIds && selectedTeamsIds.length && collabs.length) {
            let selectedCollabsIds = collabs.reduce((array, current) => {
                array.push(current.id)
                return array
            }, [])
            dispatch(getPlanning({ selectedTeamsIds, selectedCollabsIds }))
        }
    }

    /**
     * Asks before a leave is deleted, or before its deletion is requested.
     * @param {Function} onConfirm
     * @param {boolean} [isRequest] - a validated leave: the collab only asks a manager to delete it
     */
    function confirmDelete(onConfirm, isRequest = false) {
        confirmDialog({
            title: <Langs str="DELETE_LEAVE" />,
            message: <Langs str={isRequest ? "REQUEST_DELETE_LEAVE_MSG" : "DELETE_LEAVE_MSG"} />,
            danger: true,
            confirmLabel: <Langs str={isRequest ? "REQUEST_DELETE" : "DELETE"} />,
            onConfirm,
        })
    }

    function handleRequestDelete() {
        dispatch(requestDeleteLeave({ id: selectedLeaveFormData.id })).then((response) => {
            if (response['payload']['responseData']) {
                loadPlanning()
                if (selectedLeaveFormData["status"] != "PENDING")
                    showSuccessToast(<Langs str="REQUEST_SENT" />)
            }
            if (response['payload']['responseError']) {
                showErrorToast()
            }
        })
        props.onHide()
    }

    function handleSend() {
        if (formData.from && formData.to && formData.to <= formData.from) {
            setFormError("dates")
            return
        }
        if (!formData.id_collab) {
            setFormError("id_collab")
            return
        }
        if (!formData.id_sample_leave) {
            setFormError("id_sample_leave")
            return
        }
        setFormError(null)
        setSaving(true)
        dispatch(requestLeave(formData)).then((action) => {
            const payload = action && action.payload
            const data = payload && payload.responseData
            // The popup only closes once the leave is actually saved. A refusal (an overlap,
            // not enough days…) keeps it open with everything still filled in, so the one
            // wrong field can be corrected and sent again. Refusals sent as `status: 0` are
            // toasted by the requestLeave reducer; an HTTP error is not, so it is here.
            if (!data || data.status !== 1) {
                if (!data) {
                    const error = payload && payload.responseError && payload.responseError.data
                    showErrorToast(error && (error.message || error.error))
                }
                return
            }
            // The justificatif is uploaded after the fact rather than in the same request:
            // requestLeave is JSON and runs the whole solde/overlap validation, and a
            // multipart body would have to be parsed before any of that could reject it.
            if (pendingAttachment && data.id) {
                dispatch(uploadLeaveAttachment({ file: pendingAttachment, idPlanning: data.id }))
            }
            setPendingAttachment(null)
            dispatch(getCollabs({ selectedTeamsIds, isCurrent: true }))
            hide()
        }).finally(() => setSaving(false))

    }

    function loadLeaves() {
        if (selectedTeamsIds && selectedTeamsIds.length) {
            let selectedCollabsIds = collabs.reduce((array, current) => {
                array.push(current.id)
                return array
            }, [])
            dispatch(getLeaves({ selectedTeamsIds, selectedCollabsIds }))
        }
    }

    function handleCollabChange(collab) {
        if (!collab) return
        // set collab's team timezone
        let collabTimezone = config.timezone
        switch (collab.id_country) {
            case 3:
                collabTimezone = "Indian/Mauritius"
                break
            default:
                break
        }

        setTimezone(collabTimezone)

        // Work-stoppage types (Maladie, Maternité, Paternité, Décès, Mariage, Naissance, Congé
        // parental) are declared from Arrêts de travail, not requested here.
        let availableLeaves = sampleLeaves.filter(s => !s.visibility && !s.isWorkStoppage && (!s.id_country || s.id_country == collab.id_country))

        if (!isAdmin(userRoleId)) {
            availableLeaves = availableLeaves.filter((sampleLeave) => sampleLeave.roleId >= 2)
        }

        if (collab.id != userId && !isOwner(userRoleId, collab.id_country)) {
            availableLeaves = availableLeaves.filter((sampleLeave) => sampleLeave.roleId < 3)
        }

        setSelectedCollab(collab)
        setLeavesOptions(availableLeaves)

        let from = moment().tz(collabTimezone).startOf("day")
        let to = moment().tz(collabTimezone).endOf("day")

        setFormData({
            ...formData,
            id_collab: collab.id,
            id_team: collab.id_team,
            id_sample_leave: availableLeaves[0] ? availableLeaves[0].id : null,
            type: availableLeaves[0] ? availableLeaves[0].type : null,
            from: parseInt(from.format("x")),
            to: parseInt(to.format("x"))
        })
        setFormError(null)
    }

    function handleTypeChange(selectedSample) {
        if (!selectedSample) return
        setFormData({ ...formData, id_sample_leave: selectedSample.id, type: selectedSample.type })
    }

    /** The shared date field speaks dates + half days; the leave is stored as timestamps. */
    function handlePeriodChange(next) {
        if (!next.start_date || !next.end_date) return
        const { from, to } = leaveTimestampsFromPeriod(next, timezone)
        setFormData({ ...formData, from, to })
        if (formError === "dates") setFormError(null)
    }

    const handleDescriptionChange = (event) => {
        setFormData({ ...formData, description: event.target.value })
    }

    function getLeaveLabel(leaveData) {
        if (leaveData.type == 'LEAVE') return <Langs str={leaveData.type} />
        let sampleLeave = sampleLeaves.filter(s => s.id == leaveData.id_sample_leave)
        if (sampleLeave[0]) return sampleLeave[0].description
        return ""
    }

    // ── Attachments (PDF justificatifs) ─────────────────────────────────────────
    //
    // Gated on `leaves` EDIT for this leave's team — the same rule the backend enforces.
    // Deliberately not `view`: an attachment on a sick leave is a medical document, so
    // being able to see that the leave exists must not be enough to open its justificatif.
    // The check here only decides what to render; the backend is what actually protects
    // the file.
    function getLeaveSample(leaveData) {
        if (!leaveData) return null
        return sampleLeaves.find(s => s.id == leaveData.id_sample_leave) || null
    }

    // Same rule as canUseAttachments, but for the leave being composed: the type comes from
    // the form rather than from a saved row, and the team likewise.
    function canStageAttachment() {
        if (leaveModalType !== 'ADD_LEAVE') return false
        const sample = sampleLeaves.find(s => s.id == formData.id_sample_leave)
        if (!sample || !sample.allowsAttachment) return false
        return isManager()
    }

    function canUseAttachments() {
        if (leaveModalType !== 'UPDATE_LEAVE' || !selectedLeaveFormData || !selectedLeaveFormData.id) return false
        const sample = getLeaveSample(selectedLeaveFormData)
        if (!sample || !sample.allowsAttachment) return false
        return isManager()
    }

    function renderAttachments() {
        if (!canUseAttachments()) return null
        return (
            <FormRow label={<Langs str="ATTACHMENTS" />} alignTop>
                {/* Opened in a new tab rather than fetched into memory: the backend streams
                    it `inline`, so the browser's own PDF viewer handles it. */}
                <WorkStoppageAttachments
                    items={(attachments || []).map((attachment) => ({
                        key: attachment.id,
                        id: attachment.id,
                        name: attachment.original_name,
                        size: attachment.size,
                        url: NetworkManager.getInstance().getLeaveAttachmentUrl(attachment.id),
                    }))}
                    loading={attachmentsLoading}
                    busy={attachmentUploading}
                    canEdit
                    onAdd={(file) => dispatch(uploadLeaveAttachment({ file, idPlanning: selectedLeaveFormData.id }))}
                    onRemove={(item) => dispatch(deleteLeaveAttachment({ id: item.id, idPlanning: selectedLeaveFormData.id }))}
                    titleKey={null}
                    helpKey={null}
                    addKey="ADD_ATTACHMENT"
                />
            </FormRow>
        )
    }

    function renderPendingAttachment() {
        if (!canStageAttachment()) return null
        return (
            <FormRow label={<Langs str="ATTACHMENTS" />} alignTop>
                <WorkStoppageAttachments
                    items={pendingAttachment
                        ? [{ key: "pending", name: pendingAttachment.name, size: pendingAttachment.size }]
                        : []}
                    canEdit
                    onAdd={(file) => setPendingAttachment(file)}
                    onRemove={() => setPendingAttachment(null)}
                    titleKey={null}
                    helpKey={null}
                    addKey={pendingAttachment ? "PHOTO_CHANGE_FILE" : "ADD_ATTACHMENT"}
                />
            </FormRow>
        )
    }

    function renderView() {
        const fromTs = getLeaveTimestamp(selectedLeaveFormData, "from")
        const toTs = getLeaveTimestamp(selectedLeaveFormData, "to")
        const period = fromTs !== null && toTs !== null ? leavePeriodFromTimestamps(fromTs, toTs, timezone) : null
        return (
            <>
                <FormRow label={<Langs str="COLLAB" />}>
                    <Value>{selectedLeaveFormData.collab}</Value>
                </FormRow>
                <FormRow label={<Langs str="START" />}>
                    <Value>
                        {renderLeaveDate(fromTs)}
                        {period ? ` · ${Localize(periodLabelKey(period.start_period))}` : ""}
                    </Value>
                </FormRow>
                <FormRow label={<Langs str="END" />}>
                    <Value>
                        {renderLeaveDate(toTs)}
                        {period ? ` · ${Localize(periodLabelKey(period.end_period))}` : ""}
                    </Value>
                </FormRow>
                <FormRow label={<Langs str="TYPE" />}>
                    <Value><Langs str={selectedLeaveFormData.type} /></Value>
                </FormRow>
                {selectedLeaveFormData.description && (
                    <FormRow label={<Langs str="DESCRIPTION" />}>
                        <Value>{selectedLeaveFormData.description}</Value>
                    </FormRow>
                )}
                <FormRow label={<Langs str="CREATED_AT" />}>
                    <Value muted>{getLastUpdatedFromDate(selectedLeaveFormData.createdAt) || "-"}</Value>
                </FormRow>
                <FormRow label={<Langs str="CREATED_BY" />}>
                    <Value muted>{selectedLeaveFormData.createdBy}</Value>
                </FormRow>
                {renderAttachments()}
            </>
        )
    }

    function renderAdd() {
        const collabOptions = getCollabsOptions()
        const selectedCollabOption = collabOptions.find(c => c.id == formData.id_collab) || null
        const extraLeave = leavesOptions.find(l => l.type == 'EXTRA')
        return (
            <>
                <FormRow label={<Langs str="COLLAB" />} required>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={collabOptions}
                            value={selectedCollabOption}
                            onChange={(_e, collab) => handleCollabChange(collab)}
                            getOptionLabel={collabLabel}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(optionProps, collab) => (
                                <li {...optionProps} key={collab.id}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                                        <span>{collabLabel(collab)}</span>
                                        <CollabTypeChip collab={collab} />
                                    </Stack>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField {...params} sx={formFieldSx} error={formError === "id_collab"} />
                            )}
                        />
                        {selectedCollabOption && <CollabTypeChip collab={selectedCollabOption} />}
                    </Stack>
                </FormRow>
                <LeaveDateRangeField
                    value={leavePeriodFromTimestamps(formData.from, formData.to, timezone)}
                    onChange={handlePeriodChange}
                    errors={formError === "dates" ? { end_date: Localize("ADD_LEAVE_ERROR_DATE") } : {}}
                    required
                />
                <FormRow label={<Langs str="TYPE" />} required>
                    <LeaveTypeSelect
                        value={formData.id_sample_leave}
                        onChange={handleTypeChange}
                        options={leavesOptions}
                        error={formError === "id_sample_leave"}
                    />
                </FormRow>
                <FormRow label={<Langs str="DESCRIPTION" />} alignTop>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        size="small"
                        name="description"
                        inputProps={{ maxLength: 200 }}
                        value={formData.description || ""}
                        onChange={handleDescriptionChange}
                        sx={formFieldSx}
                    />
                </FormRow>
                {selectedCollab && (
                    <FormRow label={<Langs str="CPN-1" />}>
                        <Value muted>
                            {selectedCollab.solde_cp_old} <Langs str="days available till" /> {moment(selectedCollab.solde_cp_old_expiry).tz(timezone).format("DD/MM/YYYY")}
                        </Value>
                    </FormRow>
                )}
                {selectedCollab && (
                    <FormRow label={<Langs str="CPN" />}>
                        <Value muted>{selectedCollab.solde_cp_current} <Langs str="days available" /></Value>
                    </FormRow>
                )}
                {selectedCollab && leavesOptions.find(l => l.type == 'S') && (
                    <FormRow label={<Langs str="Sick Leaves" />}>
                        <Value muted>{selectedCollab.solde_sick_leaves} <Langs str="days available" /></Value>
                    </FormRow>
                )}
                {selectedCollab && extraLeave && (
                    <FormRow label={<Langs str={extraLeave.description} />}>
                        <Value muted>{selectedCollab.solde_extra_leaves} <Langs str="days available" /></Value>
                    </FormRow>
                )}
                {renderPendingAttachment()}
                {formData.from && formData.to && formData.to <= formData.from && (
                    <Alert severity="error" sx={{ borderRadius: "12px", mt: 1 }}>
                        <Langs str="ADD_LEAVE_ERROR_DATE" />
                    </Alert>
                )}
            </>
        )
    }

    function renderActions() {
        const leave = selectedLeaveFormData || {}
        const isAdminView = isOwner(userRoleId) || isManager(leave.id_team)
        const cancel = <FormButton variant="secondary" onClick={() => hide()}><Langs str="CANCEL" /></FormButton>

        if (leaveModalType === "ADD_LEAVE")
            return (
                <>
                    {cancel}
                    <FormButton onClick={() => handleSend()} loading={saving}>
                        <Langs str={isAdminView && isManager() ? "ADD" : "SEND"} />
                    </FormButton>
                </>
            )
        if (leaveModalType !== "UPDATE_LEAVE") return null

        if (isAdminView) {
            if (leave.status === "PENDING")
                return (
                    <>
                        <FormButton variant="danger" onClick={() => updateLeaveWithStatus('REJECTED')}><Langs str="REJECT" /></FormButton>
                        <FormButton onClick={() => updateLeaveWithStatus('VALIDATED')}><Langs str="VALIDATE" /></FormButton>
                    </>
                )
            if (leave.status !== 'DELETED')
                return (
                    <>
                        {cancel}
                        <FormButton variant="danger" onClick={() => confirmDelete(() => updateLeaveWithStatus('DELETED'))}><Langs str="DELETE" /></FormButton>
                    </>
                )
            return null
        }

        if (leave["id_collab"] == userId && !['DELETED', 'DELETE_REQUESTED'].includes(leave.status))
            return (
                <>
                    {cancel}
                    <FormButton variant="danger" onClick={() => confirmDelete(handleRequestDelete, leave["status"] != "PENDING")}>
                        <Langs str={leave["status"] == "PENDING" ? "DELETE" : "REQUEST_DELETE"} />
                    </FormButton>
                </>
            )
        return null
    }

    const isUpdate = !!(selectedLeaveFormData && leaveModalType == 'UPDATE_LEAVE')
    const title = isUpdate
        ? getLeaveLabel(selectedLeaveFormData)
        : (leaveModalType == 'ADD_LEAVE' ? <Langs str={isManager() ? "Add New Leave" : "Request New Leave"} /> : "")

    return (
        <FormDialog
            open={!!props.show}
            onClose={() => hide()}
            title={title}
            titleAdornment={isUpdate ? (
                <Chip
                    size="small"
                    color={STATUS_CHIP_COLORS[getTagClass(selectedLeaveFormData.status)] || "default"}
                    label={<Langs str={selectedLeaveFormData.status} />}
                    sx={{ fontWeight: 600 }}
                />
            ) : null}
            fullScreen={isMobile === 2}
            actions={renderActions()}
        >
            {isUpdate ? renderView() : (leaveModalType == 'ADD_LEAVE' ? renderAdd() : null)}
        </FormDialog>
    )
}
